import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import bundleDe from './bundle.de.json';
import bundleEn from './bundle.en.json';

// ============================================
// Mehrsprachigkeit (Projekt 2026-09-17) – Laufzeit des Webs.
//
// - Eingebaute Bundles (bundle.de/en.json, aus dem Katalog generiert) sind sofort da.
// - Beim Start wird das Backend-Bundle geholt, wenn dessen Version neuer ist als der
//   Cache in localStorage ('dc-i18n-<lang>'). Wartezeit maximal REMOTE_WAIT_MS, danach
//   rendert die App mit dem eingebauten/gecachten Stand; ein spät eintreffendes Bundle
//   landet im Cache für den nächsten Start (keine Remounts mitten in der Nutzung).
// - Sprache: localStorage 'dc-lang' → Gerätesprache (de* → de, sonst en).
// - Texte im Code: t('web.bereich.key') (Import aus diesem Modul), Platzhalter {{name}}.
// ============================================

export type Lang = 'de' | 'en';
export const SUPPORTED_LANGS: Lang[] = ['de', 'en'];
export const LANG_STORAGE_KEY = 'dc-lang';
const CACHE_PREFIX = 'dc-i18n-';
const REMOTE_WAIT_MS = 1500;
const API_BASE = (process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'https://dartcloud-backend.de').replace(/\/$/, '');

/** 'de-DE' → 'de', 'en-GB' → 'en', Unbekanntes → null (pure). */
export const normalizeLang = (value: unknown): Lang | null => {
  if (typeof value !== 'string') return null;
  const code = value.trim().toLowerCase().split(/[-_;,]/)[0];
  return (SUPPORTED_LANGS as string[]).includes(code) ? (code as Lang) : null;
};

/** Gerätesprache: Deutsch → de, alles andere → en (Marcels Regel, pure). */
export const defaultLangForDevice = (deviceLocale: unknown): Lang => (normalizeLang(deviceLocale) === 'de' ? 'de' : 'en');

/** Startsprache aus gespeicherter Wahl oder Gerät (pure, Eingaben injizierbar). */
export const detectInitialLang = (stored: unknown, deviceLocale: unknown): Lang =>
  normalizeLang(stored) ?? defaultLangForDevice(deviceLocale);

export const readStoredLang = (): Lang | null => {
  try {
    return normalizeLang(localStorage.getItem(LANG_STORAGE_KEY));
  } catch {
    return null;
  }
};

export const writeStoredLang = (lang: Lang): void => {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    /* privater Modus o. ä. */
  }
};

interface CachedBundle { version: number; translations: Record<string, string> }

const readCache = (lang: Lang): CachedBundle | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + lang);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.version !== 'number' || typeof parsed?.translations !== 'object') return null;
    return parsed as CachedBundle;
  } catch {
    return null;
  }
};

const writeCache = (lang: Lang, bundle: CachedBundle): void => {
  try {
    localStorage.setItem(CACHE_PREFIX + lang, JSON.stringify(bundle));
  } catch {
    /* Speicher voll / blockiert */
  }
};

/** Soll das Server-Bundle übernommen werden? (pure) */
export const shouldApplyRemote = (remoteVersion: unknown, cachedVersion: number | null): boolean =>
  typeof remoteVersion === 'number' && remoteVersion > 0 && (cachedVersion === null || remoteVersion > cachedVersion);

const applyBundle = (lang: Lang, translations: Record<string, string>): void => {
  i18next.addResourceBundle(lang, 'translation', translations, true, true);
};

// Synchron beim Modul-Import initialisieren: Module mit t()-Aufrufen auf oberster Ebene
// (Label-Konstanten) werden VOR initI18n() ausgewertet – deshalb muss der Katalog
// stehen, sobald dieses Modul geladen ist (index.tsx importiert es als erstes).
const initialLang = detectInitialLang(readStoredLang(), typeof navigator !== 'undefined' ? navigator.language : null);
i18next.use(initReactI18next).init({
  resources: { de: { translation: bundleDe }, en: { translation: bundleEn } },
  lng: initialLang,
  fallbackLng: 'de',
  interpolation: { escapeValue: false, prefix: '{{', suffix: '}}' },
  returnNull: false,
  returnEmptyString: false,
  keySeparator: false,
  nsSeparator: false,
  initImmediate: false,
});
for (const l of SUPPORTED_LANGS) {
  const cached = readCache(l);
  if (cached) applyBundle(l, cached.translations);
}
if (typeof document !== 'undefined') document.documentElement.lang = initialLang;

/** Backend-Bundle holen und anwenden, wenn neuer als der Cache. */
export const refreshFromServer = async (lang: Lang): Promise<boolean> => {
  const cached = readCache(lang);
  const res = await fetch(`${API_BASE}/api/i18n/bundle/${lang}`, { headers: { Accept: 'application/json' } });
  if (!res.ok) return false;
  const data = await res.json();
  if (!shouldApplyRemote(data?.version, cached?.version ?? null)) return false;
  const translations = data?.translations && typeof data.translations === 'object' ? data.translations : {};
  writeCache(lang, { version: data.version, translations });
  applyBundle(lang, translations);
  return true;
};

/** Vor dem ersten Render (index.tsx): kurz auf ein frischeres Server-Bundle warten. Nie werfend. */
export const initI18n = async (): Promise<Lang> => {
  try {
    await Promise.race([
      refreshFromServer(initialLang).catch(() => false),
      new Promise((resolve) => setTimeout(resolve, REMOTE_WAIT_MS)),
    ]);
  } catch {
    /* offline o. ä. */
  }
  return initialLang;
};

/**
 * Sprache wechseln: Wahl speichern und Seite neu laden. Ein Reload ist bewusst –
 * Label-Konstanten auf Modulebene werden nur beim Laden übersetzt, und ein
 * Sprachwechsel ist selten. Vorher das Bundle der Zielsprache in den Cache holen.
 */
export const changeLanguage = async (lang: Lang): Promise<void> => {
  writeStoredLang(lang);
  try {
    await Promise.race([refreshFromServer(lang).catch(() => false), new Promise((r) => setTimeout(r, REMOTE_WAIT_MS))]);
  } catch {
    /* egal – eingebautes Bundle reicht */
  }
  if (typeof window !== 'undefined') window.location.reload();
};

export const currentLang = (): Lang => normalizeLang(i18next.language) ?? 'de';

/** Übersetzungsfunktion für den Code: t('web.home.title', { name }) */
export const t = (key: string, params?: Record<string, unknown>): string =>
  // Parameter NUR als Interpolationswerte übergeben – Namen wie lng, ns, count oder replace
  // dürfen nie als i18next-Optionen wirken (Absturz „lng.toLowerCase“, Turnierseite 2026-09-17)
  i18next.t(key, params ? ({ replace: params } as any) : undefined) as string;

export default i18next;
